import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useListClasses,
  useArchiveClass,
  useListTimetables,
  useArchiveTimetable,
  getListClassesQueryKey,
  getListTimetablesQueryKey,
} from "@workspace/api-client-react";
import {
  useArchivedProgramRegisters,
  useRestoreRegister,
} from "@/lib/program-registration";
import {
  Archive,
  Package,
  ChevronDown,
  ChevronRight,
  ArchiveRestore,
  Loader2,
  BookOpen,
  ClipboardList,
  CalendarDays,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { Link } from "wouter";
import { format, parseISO } from "date-fns";

// ── Archive years hook ─────────────────────────────────────────────────────────

function useArchiveApi() {
  const { getToken } = useAuth();
  return async function apiFetch<T>(
    url: string,
    options?: RequestInit,
  ): Promise<T> {
    const token = await getToken();
    const res = await fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err: Error & { status?: number } = new Error(
        (body as { error?: string }).error ?? "Request failed",
      );
      err.status = res.status;
      throw err;
    }
    return res.json() as Promise<T>;
  };
}

const ARCHIVE_YEARS_KEY = ["archive-years"] as const;

function useArchiveYears() {
  const apiFetch = useArchiveApi();
  const { isLoaded } = useAuth();
  return useQuery({
    queryKey: ARCHIVE_YEARS_KEY,
    queryFn: () => apiFetch<{ years: string[] }>("/api/archive/years"),
    select: (data) => data.years,
    enabled: isLoaded,
  });
}

// ── Minimal local types ────────────────────────────────────────────────────────

interface ArchivedClass {
  id: number;
  name: string;
  yearLevel?: string | null;
  schoolYear?: string | null;
  archived?: boolean;
}

interface ArchivedTimetable {
  id: number;
  name: string;
  startDate: string | Date;
  endDate: string | Date;
  archived?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Extract a 4-digit year string from an ISO date string or Date object.
const yearOf = (d: string | Date | null | undefined): string => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 4);
  return d.getFullYear().toString();
};

const fmtDate = (d: string | Date | null | undefined): string => {
  if (!d) return "";
  try {
    const date = typeof d === "string" ? parseISO(d) : d;
    return format(date, "d MMM yyyy");
  } catch {
    return String(d).slice(0, 10);
  }
};

// ── Archive page ──────────────────────────────────────────────────────────────

export default function ArchivePage() {
  const { data: years = [], isLoading: yearsLoading } = useArchiveYears();
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Archived classes — loaded lazily once a year box is first opened.
  // includeArchived=1 returns both active and archived rows; we filter
  // to archived===true + matching schoolYear on the client side.
  const { data: allArchivedClassesRaw = [], isLoading: classesLoading } =
    useListClasses(
      { includeArchived: "1" },
      { query: { enabled: !!selectedYear } },
    );
  const allArchivedClasses = allArchivedClassesRaw as unknown as ArchivedClass[];

  // Archived program registers — all archived rows, filter by schoolYear
  const { data: allArchivedRegisters = [], isLoading: registersLoading } =
    useArchivedProgramRegisters({ enabled: !!selectedYear });

  // All timetables (GET /timetables returns active + archived together)
  const { data: allTimetablesRaw = [] } = useListTimetables();
  const allTimetables = allTimetablesRaw as unknown as ArchivedTimetable[];

  const restoreClass = useArchiveClass();
  const restoreRegister = useRestoreRegister();
  const restoreTimetable = useArchiveTimetable();

  const contentLoading = classesLoading || registersLoading;

  function toggleYear(year: string) {
    setSelectedYear((prev) => (prev === year ? null : year));
  }

  const invalidateYears = () =>
    queryClient.invalidateQueries({ queryKey: ARCHIVE_YEARS_KEY });

  async function handleRestoreClass(cls: ArchivedClass) {
    try {
      await restoreClass.mutateAsync({
        classId: cls.id,
        data: { archived: false },
      });
      queryClient.invalidateQueries({
        queryKey: getListClassesQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getListClassesQueryKey({ includeArchived: "1" }),
      });
      invalidateYears();
      toast({ title: `"${cls.name}" restored to active classes` });
    } catch (err) {
      toast({
        title: "Couldn't restore class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleRestoreRegister(reg: { id: number; title: string }) {
    try {
      await restoreRegister.mutateAsync(reg.id);
      invalidateYears();
      toast({ title: `"${reg.title}" restored to active programs` });
    } catch (err) {
      toast({
        title: "Couldn't restore program register",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleRestoreTimetable(t: ArchivedTimetable) {
    try {
      await restoreTimetable.mutateAsync({
        timetableId: t.id,
        data: { archived: false },
      });
      queryClient.invalidateQueries({
        queryKey: getListTimetablesQueryKey(),
      });
      invalidateYears();
      toast({ title: `"${t.name}" restored to active diaries` });
    } catch (err) {
      toast({
        title: "Couldn't restore diary",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  // Derived entity lists for the currently selected year
  const yearClasses = allArchivedClasses.filter(
    (c) => c.archived === true && c.schoolYear === selectedYear,
  );
  const yearRegisters = allArchivedRegisters.filter(
    (r) => r.schoolYear === selectedYear,
  );
  const yearTimetables = allTimetables.filter(
    (t) => t.archived === true && yearOf(t.startDate) === selectedYear,
  );

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Archive</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Past years are stored here as labelled boxes. Each box shows the
          archived classes, program registers, and diaries for that year.
        </p>
        <Link
          href="/app/settings?tab=school-year"
          className="inline-flex items-center gap-1.5 mt-3 text-sm transition-colors group"
        >
          <CalendarDays className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">Ready to close out the current year?</span>
          <span className="text-primary font-medium group-hover:underline">Go to School Year settings.</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-primary" />
        </Link>
      </div>

      {/* Loading years */}
      {yearsLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!yearsLoading && years.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-2xl"
          data-testid="archive-empty-state"
        >
          <Archive className="h-12 w-12 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-1">
            No archived years yet
          </h2>
          <p className="text-sm text-gray-500 max-w-xs">
            Archive boxes appear here once you archive classes, program
            registers, or diaries and label them with a school year.
          </p>
        </div>
      )}

      {/* Year boxes */}
      {!yearsLoading && years.length > 0 && (
        <div className="space-y-3" data-testid="archive-year-list">
          {years.map((year) => {
            const isOpen = selectedYear === year;
            return (
              <div
                key={year}
                className="border rounded-xl overflow-hidden bg-white shadow-sm"
                data-testid={`archive-year-${year}`}
              >
                {/* Year card header */}
                <button
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => toggleYear(year)}
                  data-testid={`button-expand-year-${year}`}
                >
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <span className="text-lg font-semibold text-gray-900">
                      {year}
                    </span>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {/* Year box contents */}
                {isOpen && (
                  <div className="border-t divide-y">
                    {contentLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        {/* Classes */}
                        <YearSection
                          icon={
                            <BookOpen className="h-4 w-4 text-blue-600" />
                          }
                          title="Classes"
                          count={yearClasses.length}
                          emptyLabel="No archived classes yet. After year-end closeout, classes from this year will appear here."
                        >
                          {yearClasses.map((cls) => (
                            <EntityRow
                              key={cls.id}
                              primary={cls.name}
                              secondary={
                                cls.yearLevel
                                  ? `Year ${cls.yearLevel}`
                                  : undefined
                              }
                              onRestore={() => handleRestoreClass(cls)}
                              restorePending={restoreClass.isPending}
                              data-testid={`archive-class-row-${cls.id}`}
                            />
                          ))}
                        </YearSection>

                        {/* Program registers */}
                        <YearSection
                          icon={
                            <ClipboardList className="h-4 w-4 text-purple-600" />
                          }
                          title="Program Registers"
                          count={yearRegisters.length}
                          emptyLabel="No archived program registers yet. After closeout, this year's programs will appear here."
                        >
                          {yearRegisters.map((reg) => {
                            const meta = [reg.subject, reg.yearGroup, reg.term]
                              .filter(Boolean)
                              .join(" · ");
                            return (
                              <EntityRow
                                key={reg.id}
                                primary={reg.title}
                                secondary={meta || undefined}
                                onRestore={() => handleRestoreRegister(reg)}
                                restorePending={restoreRegister.isPending}
                                data-testid={`archive-register-row-${reg.id}`}
                              />
                            );
                          })}
                        </YearSection>

                        {/* Diaries (timetables) */}
                        <YearSection
                          icon={
                            <CalendarDays className="h-4 w-4 text-green-600" />
                          }
                          title="Diaries"
                          count={yearTimetables.length}
                          emptyLabel="No archived diaries yet. After closeout, this year's timetable/diary structure will appear here."
                        >
                          {yearTimetables.map((t) => (
                            <EntityRow
                              key={t.id}
                              primary={t.name}
                              secondary={
                                t.startDate && t.endDate
                                  ? `${fmtDate(t.startDate)} – ${fmtDate(t.endDate)}`
                                  : undefined
                              }
                              onRestore={() => handleRestoreTimetable(t)}
                              restorePending={restoreTimetable.isPending}
                              data-testid={`archive-timetable-row-${t.id}`}
                            />
                          ))}
                        </YearSection>

                        {/* Exports hint */}
                        <div className="px-5 py-3 bg-gray-50 flex items-center justify-between gap-3">
                          <p className="text-xs text-muted-foreground">
                            Need diary or records exports for {year}? Open
                            Exports and choose the {year} date range.
                          </p>
                          <Link href="/app/exports">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs gap-1.5 flex-shrink-0"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              Exports
                            </Button>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function YearSection({
  icon,
  title,
  count,
  emptyLabel,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        {count > 0 && (
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            {count}
          </Badge>
        )}
      </div>
      {count === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      ) : (
        <div className="border rounded-lg divide-y">{children}</div>
      )}
    </div>
  );
}

function EntityRow({
  primary,
  secondary,
  onRestore,
  restorePending,
  "data-testid": testId,
}: {
  primary: string;
  secondary?: string;
  onRestore: () => void;
  restorePending: boolean;
  "data-testid"?: string;
}) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 gap-2"
      data-testid={testId}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{primary}</p>
        {secondary && (
          <p className="text-xs text-muted-foreground truncate">{secondary}</p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-xs gap-1.5 text-blue-600 flex-shrink-0"
        onClick={onRestore}
        disabled={restorePending}
        data-write-action
      >
        <ArchiveRestore className="h-3.5 w-3.5" />
        Restore
      </Button>
    </div>
  );
}
